import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Ban,
  Building,
  Calendar,
  CheckCircle,
  Info,
  Edit,
  Eye,
  PlayCircle,
  Plus,
  RefreshCw,
  Search,
  Send,
  Users,
  X,
  XCircle,
} from "lucide-react";
import {
  batchApi,
  Batch,
  BatchChangeRequestStats,
  BatchChangeRequestSummary,
  BatchFlags,
  Center,
  centerApi,
  Mobilisation,
  mobilisationApi,
  TraineeConsentStatus,
  TraineeRegistrationData,
  Trainer,
  trainerApi,
  traineeRegistrationApi,
  attendanceApi,
} from "../api";
import { useAuth } from "../hooks/useAuth";
import { TRAINER_TAG_BADGE_STYLES } from "../constants/trainers";
import ModalPortal from "./ModalPortal";

type Toast = {
  type: "success" | "error";
  message: string;
};

type FormState = {
  mobilisation_id: number;
  center_id: number;
  trainer_id: number;
  is_center_infra_ready: boolean;
  is_recruiter_engaged: boolean;
  launchpad_session_dates: string[];
  basecamp_dates: string[];
  domain_training_dates: string[];
  ojt_dates: string[];
  pre_assessment_start_datetime: string;
  post_assessment_datetime: string;
  batch_launch_date: string;
  placement_drive_date: string;
};

const EMPTY_FORM: FormState = {
  mobilisation_id: 0,
  center_id: 0,
  trainer_id: 0,
  is_center_infra_ready: false,
  is_recruiter_engaged: false,
  launchpad_session_dates: ["", "", "", ""],
  basecamp_dates: [""],
  domain_training_dates: [""],
  ojt_dates: [""],
  pre_assessment_start_datetime: "",
  post_assessment_datetime: "",
  batch_launch_date: "",
  placement_drive_date: "",
};

const asDateOnly = (value?: string | null): string => {
  if (!value) return "";
  return value.includes("T") ? value.slice(0, 10) : value;
};

const toDatetimeLocal = (value?: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.length > 16 ? value.slice(0, 16) : value;
  }
  const offsetMinutes = date.getTimezoneOffset() * -1;
  const adjusted = new Date(date.getTime() + offsetMinutes * 60 * 1000);
  return adjusted.toISOString().slice(0, 16);
};

const toIsoString = (value: string): string => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toISOString();
};

const normalizeDateList = (list: string[] = []): string[] => {
  return Array.from(new Set(list.filter(Boolean).map((item) => item.trim()))).sort();
};

const todayIstDateString = (): string => {
  const now = new Date();
  const shifted = new Date(now.getTime() + 330 * 60 * 1000);
  return shifted.toISOString().slice(0, 10);
};

type PartTwoTab = "registered" | "finalList" | "consent";

const PART_TWO_TABS: ReadonlyArray<{ id: PartTwoTab; label: string }> = [
  { id: "registered", label: "Registered" },
  { id: "finalList", label: "Final list" },
  { id: "consent", label: "Consent" },
];

type DateInputGroupProps = {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  requiredCount?: number;
  disabled?: boolean;
};

const DateInputGroup: React.FC<DateInputGroupProps> = ({ label, value, onChange, requiredCount, disabled }) => {
  const handleChange = (index: number, nextValue: string) => {
    const copy = [...value];
    copy[index] = nextValue;
    onChange(copy);
  };

  const handleAdd = () => {
    onChange([...value, ""]);
  };

  const handleRemove = (index: number) => {
    const filtered = value.filter((_, position) => position !== index);
    onChange(filtered.length ? filtered : [""]);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {value.map((entry, index) => (
        <div key={label + index} className="flex items-center gap-2">
          <input
            type="date"
            value={entry}
            required={requiredCount ? index < requiredCount : false}
            disabled={disabled}
            onChange={(event) => handleChange(index, event.target.value)}
            className="flex-1 rounded border px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          {value.length > 1 && !disabled && (
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="rounded-full p-2 text-red-500 transition hover:bg-red-50"
            >
              <XCircle className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button
          type="button"
          onClick={handleAdd}
          className="text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          + Add another date
        </button>
      )}
    </div>
  );
};

const formatDisplayValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined || value === "") return "\u2014";
  const stringValue = String(value);
  if (stringValue.includes("T")) {
    const date = new Date(stringValue);
    return Number.isNaN(date.getTime()) ? stringValue : date.toLocaleString();
  }
  return stringValue;
};

const CHANGE_FIELD_LABELS: Record<string, string> = {
  trainer_id: "Trainer",
  center_id: "Center",
  is_center_infra_ready: "Center infra ready",
  is_recruiter_engaged: "Recruiter engaged",
  launchpad_session_dates: "Launchpad dates",
  basecamp_dates: "Basecamp dates",
  domain_training_dates: "Domain training dates",
  ojt_dates: "OJT dates",
  pre_assessment_start_datetime: "Pre-assessment start",
  post_assessment_datetime: "Post-assessment",
  batch_launch_date: "Batch launch",
  placement_drive_date: "Placement drive",
  note: "Request note",
};
const BatchManagement: React.FC = () => {
  const { user } = useAuth();
  const isSystemAdmin = user?.role === "SYSTEM_ADMIN" || user?.role === "OPERATIONS_ADMIN";
  const isCenterAdmin = user?.role === "CENTER_ADMIN" || user?.role === "PROJECT_MANAGER";
  const isMobilisationPartner = user?.role === "MOBILISATION_PARTNER";
  const canEditProposalDraft = isCenterAdmin || isMobilisationPartner || isSystemAdmin;
  const canSubmitProposal = isCenterAdmin;

  const [activePartTwoTab, setActivePartTwoTab] = useState<PartTwoTab>("registered");

  const [toast, setToast] = useState<Toast | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [search, setSearch] = useState<string>("");
  const [proposalFilter, setProposalFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorDetails, setFormErrorDetails] = useState<string | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState<boolean>(false);

  const [mobilisations, setMobilisations] = useState<Mobilisation[]>([]);
  const [centers, setCenters] = useState<Center[]>([]);
  const [trainers, setTrainers] = useState<Trainer[]>([]);

  const [formState, setFormState] = useState<FormState>(EMPTY_FORM);
  const [editingBatch, setEditingBatch] = useState<Batch | null>(null);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const selectedTrainer = useMemo(
    () => trainers.find((trainer) => trainer.trainer_id === formState.trainer_id),
    [trainers, formState.trainer_id]
  );

  const changeBaselineRef = useRef<FormState | null>(null);
  const [changeMode, setChangeMode] = useState<boolean>(false);
  const [changeNote, setChangeNote] = useState<string>("");
  const [changeSubmitting, setChangeSubmitting] = useState<boolean>(false);
  const [changeReviewComment, setChangeReviewComment] = useState<string>("");
  const [changeReviewLoading, setChangeReviewLoading] = useState<boolean>(false);

  const [registeredTrainees, setRegisteredTrainees] = useState<TraineeRegistrationData[]>([]);
  const [selectedTraineeIds, setSelectedTraineeIds] = useState<number[]>([]);
  const [finalTrainees, setFinalTrainees] = useState<TraineeConsentStatus[]>([]);
  const [traineeSearch, setTraineeSearch] = useState<string>("");
  const [sendingConsent, setSendingConsent] = useState<boolean>(false);
  const [launchpadAttendance, setLaunchpadAttendance] = useState<Record<number, { present: number; recorded: number }>>({});
  const [launchpadSessionCount, setLaunchpadSessionCount] = useState<number>(0);
  const totalLaunchpadSessions = useMemo(() => {
    if (launchpadSessionCount > 0) return launchpadSessionCount;
    const dates = editingBatch?.launchpad_session_dates;
    if (Array.isArray(dates)) {
      return dates.filter(Boolean).length;
    }
    return 0;
  }, [launchpadSessionCount, editingBatch]);

  const isCancelled = Boolean(
    editingBatch &&
      (editingBatch.status === "Cancelled" || editingBatch.batch_proposal_status === "Cancelled")
  );
  const proposalApproved = editingBatch?.batch_proposal_status === "Approved";
  const stepTwoLocked = (proposalApproved && !changeMode) || isCancelled;
  const baselineLaunchpadDates = useMemo(() => {
    const baseline =
      changeBaselineRef.current?.launchpad_session_dates ?? editingBatch?.launchpad_session_dates ?? [];
    return baseline.filter(Boolean).slice().sort();
  }, [editingBatch?.launchpad_session_dates]);
  const earliestBaselineLaunchpad = baselineLaunchpadDates[0] ?? "";
  const todayIstDate = todayIstDateString();
  const launchpadEditsLocked = isCancelled
    ? true
    : proposalApproved && earliestBaselineLaunchpad
    ? todayIstDate > earliestBaselineLaunchpad
    : false;

  // Tabs just like CenterManagement
  const [selectedTab, setSelectedTab] = useState<'details' | 'proposal'>('details');
  const [showAllApprovals, setShowAllApprovals] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  const showToast = useCallback((message: string, type: Toast["type"]) => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const loadLaunchpadAttendance = useCallback(async (batchId: number, totalSessions: number) => {
    setLaunchpadSessionCount(totalSessions);
    setLaunchpadAttendance({});
    if (!batchId) {
      return;
    }
    try {
      const attendanceRes = await attendanceApi.getBatchAttendance(String(batchId));
      const records = Array.isArray(attendanceRes?.data) ? attendanceRes.data : [];
      if (!records.length) {
        setLaunchpadAttendance({});
        return;
      }
      const map: Record<number, { present: number; recorded: number }> = {};
      const dedupe = new Set<string>();
      records.forEach((record: any) => {
        const typeValue = typeof record.session_type === "string" ? record.session_type.toUpperCase() : "";
        if (typeValue !== "LAUNCHPAD") return;
        const dateValue =
          typeof record.session_date === "string"
            ? record.session_date.slice(0, 10)
            : new Date(record.session_date).toISOString().slice(0, 10);
        const key = `${record.trainee_id}-${dateValue}`;
        if (dedupe.has(key)) return;
        dedupe.add(key);
        const existing = map[record.trainee_id] ?? { present: 0, recorded: 0 };
        if (record.present) {
          existing.present += 1;
        }
        existing.recorded += 1;
        map[record.trainee_id] = existing;
      });
      setLaunchpadAttendance(map);
    } catch (error) {
      console.error("Failed to load launchpad attendance", error);
      setLaunchpadAttendance({});
    }
  }, []);

  const refreshBatches = useCallback(async () => {
    setLoading(true);
    try {
      const response = await batchApi.getBatches({
        page: 1,
        pageSize: 9999,
        userRole: user?.role,
        mpId: user?.mp_id ?? undefined,
      });
      if (response.success && response.data) {
        const data = response.data;
        const scopedData =
          user?.role === "CENTER_ADMIN" || user?.role === "PROJECT_MANAGER"
            ? data.filter((batch) => batch.mobilisation?.center_admin_id === user.user_id)
            : data;
        setBatches(scopedData);
      }
    } catch (error) {
      console.error("Failed to load batches", error);
      showToast("Unable to load batches right now.", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast, user?.role, user?.mp_id, user?.user_id]);

  const loadReferenceData = useCallback(async () => {
    try {
      await Promise.all([
        mobilisationApi
          .getMobilisations({ limit: 500 })
          .then((response) => {
            if (response.success && response.data) setMobilisations(response.data);
          })
          .catch(() => undefined),
        centerApi
          .getBPCenters()
          .then((response) => {
            if (response.success && response.data) setCenters(response.data);
          })
          .catch(() => undefined),
        trainerApi
          .getBPTrainers()
          .then((response) => {
            if (response.success && response.data) setTrainers(response.data);
          })
          .catch(() => undefined),
      ]);
    } catch (error) {
      console.error("Failed to load reference data", error);
    }
  }, []);

  useEffect(() => {
    refreshBatches();
    loadReferenceData();
  }, [refreshBatches, loadReferenceData]);
  useEffect(() => {
    if (formError) {
      setShowErrorDetails(false);
      if (formError.includes("Details:")) {
        setFormErrorDetails(formError);
      }
    } else {
      setFormErrorDetails(null);
    }
  }, [formError]);

  const resetForm = useCallback(() => {
    setFormState(EMPTY_FORM);
    changeBaselineRef.current = null;
    setChangeMode(false);
    setChangeNote("");
    setChangeSubmitting(false);
    setChangeReviewComment("");
    setChangeReviewLoading(false);
    setRegisteredTrainees([]);
    setSelectedTraineeIds([]);
    setFinalTrainees([]);
    setTraineeSearch("");
    setLaunchpadAttendance({});
    setLaunchpadSessionCount(0);
    setFormError(null);
    setFormErrorDetails(null);
    setShowErrorDetails(false);
  }, []);

  const closeForm = useCallback(() => {
    setIsFormOpen(false);
    setEditingBatch(null);
    setCancelDialogOpen(false);
    setCancelLoading(false);
    setActivePartTwoTab("registered");
    setFormError(null);
    setFormErrorDetails(null);
    setShowErrorDetails(false);
    resetForm();
  }, [resetForm]);
  const applyBatchToForm = useCallback((batch: Batch) => {
    const launchpad = normalizeDateList(batch.launchpad_session_dates);
    const basecamp = normalizeDateList(
      batch.basecamp_dates_actual && batch.basecamp_dates_actual.length
        ? batch.basecamp_dates_actual
        : batch.basecamp_dates_planned
    );
    const domain = normalizeDateList(
      batch.domain_training_dates_actual && batch.domain_training_dates_actual.length
        ? batch.domain_training_dates_actual
        : batch.domain_training_dates_planned
    );
    const ojt = normalizeDateList(
      batch.ojt_dates_actual && batch.ojt_dates_actual.length ? batch.ojt_dates_actual : batch.ojt_dates_planned
    );

    const preStart =
      batch.pre_assessment_start_datetime_effective ||
      batch.pre_assessment_start_datetime_actual ||
      batch.pre_assessment_start_datetime_planned ||
      "";
    const postStart =
      batch.post_assessment_datetime_effective ||
      batch.post_assessment_datetime_actual ||
      batch.post_assessment_datetime_planned ||
      "";

    const nextForm: FormState = {
      mobilisation_id: batch.mobilisation_id,
      center_id: batch.center_id,
      trainer_id: batch.trainer_id,
      is_center_infra_ready: batch.is_center_infra_ready,
      is_recruiter_engaged: batch.is_recruiter_engaged,
      launchpad_session_dates: launchpad.length ? launchpad : ["", "", "", ""],
      basecamp_dates: basecamp.length ? basecamp : [""],
      domain_training_dates: domain.length ? domain : [""],
      ojt_dates: ojt.length ? ojt : [""],
      pre_assessment_start_datetime: toDatetimeLocal(preStart),
      post_assessment_datetime: toDatetimeLocal(postStart),
      batch_launch_date: asDateOnly(batch.batch_launch_date_actual || batch.batch_launch_date_planned),
      placement_drive_date: asDateOnly(batch.placement_drive_date_actual || batch.placement_drive_date_planned),
    };

    setFormState(nextForm);
    changeBaselineRef.current = { ...nextForm };

    const consented = batch.registration_detials
      ? batch.registration_detials
        .filter((entry: any) => entry.consent_mail_sent)
        .map((entry: any) => ({
          trainee_id: Number(entry.trainee_id),
          full_name: entry.full_name,
          email: entry.email,
          status: "sent" as const,
        }))
      : [];

    setFinalTrainees(consented);
    setSelectedTraineeIds(consented.map((entry) => Number(entry.trainee_id)));
  }, []);

  const openCreateModal = useCallback(() => {
    resetForm();
    setEditingBatch(null);
    setCancelDialogOpen(false);
    setCancelLoading(false);
    setActivePartTwoTab("registered");
    setFormError(null);
    setFormErrorDetails(null);
    setShowErrorDetails(false);
    setIsFormOpen(true);
  }, [resetForm]);

  const openEditModal = useCallback(
    (batch: Batch) => {
      setCancelDialogOpen(false);
      setCancelLoading(false);
      setEditingBatch(batch);
      applyBatchToForm(batch);
      setActivePartTwoTab("registered");
      setFormError(null);
      setFormErrorDetails(null);
      setShowErrorDetails(false);
      setIsFormOpen(true);
    },
    [applyBatchToForm]
  );
  const filteredBatches = useMemo(() => {
    return batches.filter((batch) => {
      const textMatch = search
        ? [batch.batch_code, batch.mobilisation_name, batch.assignedCenterName, batch.assignedTrainerName]
          .filter(Boolean)
          .some((value) => value && value.toLowerCase().includes(search.toLowerCase()))
        : true;
      const proposalMatch = proposalFilter ? batch.batch_proposal_status === proposalFilter : true;
      const statusMatch = statusFilter ? batch.status === statusFilter : true;
      return textMatch && proposalMatch && statusMatch;
    });
  }, [batches, search, proposalFilter, statusFilter]);

  const handleFormFieldChange = useCallback((event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const target = event.target as HTMLInputElement | HTMLSelectElement;
    const { name, value } = target;
    setFormState((previous) => {
      const next = { ...previous } as FormState;
      if (target instanceof HTMLInputElement && target.type === "checkbox") {
        (next as any)[name] = target.checked;
      } else if (name === "mobilisation_id" || name === "center_id" || name === "trainer_id") {
        (next as any)[name] = Number(value);
      } else {
        (next as any)[name] = value;
      }
      return next;
    });
  }, []);

  const handleArrayFieldChange = useCallback((key: keyof FormState, nextValue: string[]) => {
    setFormState((previous) => ({
      ...previous,
      [key]: nextValue,
    }));
  }, []);

  const sanitizedDates = useCallback((list: string[]) => normalizeDateList(list), []);
  const buildChangeRequestPayload = useCallback(() => {
    if (!changeBaselineRef.current) {
      return null;
    }

    const baseline = changeBaselineRef.current;
    const diff: Record<string, unknown> = {};

    if (formState.trainer_id !== baseline.trainer_id) diff.trainer_id = formState.trainer_id;
    if (formState.center_id !== baseline.center_id) diff.center_id = formState.center_id;
    if (formState.is_center_infra_ready !== baseline.is_center_infra_ready) {
      diff.is_center_infra_ready = formState.is_center_infra_ready;
    }
    if (formState.is_recruiter_engaged !== baseline.is_recruiter_engaged) {
      diff.is_recruiter_engaged = formState.is_recruiter_engaged;
    }

    const launchpad = sanitizedDates(formState.launchpad_session_dates);
    if (!launchpadEditsLocked && launchpad.join() !== sanitizedDates(baseline.launchpad_session_dates).join()) {
      diff.launchpad_session_dates = launchpad;
    }

    const basecamp = sanitizedDates(formState.basecamp_dates);
    if (basecamp.join() !== sanitizedDates(baseline.basecamp_dates).join()) {
      diff.basecamp_dates = basecamp;
    }

    const domain = sanitizedDates(formState.domain_training_dates);
    if (domain.join() !== sanitizedDates(baseline.domain_training_dates).join()) {
      diff.domain_training_dates = domain;
    }

    const ojt = sanitizedDates(formState.ojt_dates);
    if (ojt.join() !== sanitizedDates(baseline.ojt_dates).join()) {
      diff.ojt_dates = ojt;
    }

    const baselinePreIso = baseline.pre_assessment_start_datetime ? toIsoString(baseline.pre_assessment_start_datetime) : "";
    const currentPreIso = formState.pre_assessment_start_datetime ? toIsoString(formState.pre_assessment_start_datetime) : "";
    if (currentPreIso && currentPreIso !== baselinePreIso) {
      diff.pre_assessment_start_datetime = currentPreIso;
    }

    const baselinePostIso = baseline.post_assessment_datetime ? toIsoString(baseline.post_assessment_datetime) : "";
    const currentPostIso = formState.post_assessment_datetime ? toIsoString(formState.post_assessment_datetime) : "";
    if (currentPostIso && currentPostIso !== baselinePostIso) {
      diff.post_assessment_datetime = currentPostIso;
    }

    if (formState.batch_launch_date && formState.batch_launch_date !== baseline.batch_launch_date) {
      diff.batch_launch_date = formState.batch_launch_date;
    }
    if (formState.placement_drive_date && formState.placement_drive_date !== baseline.placement_drive_date) {
      diff.placement_drive_date = formState.placement_drive_date;
    }

    if (!Object.keys(diff).length) return null;
    if (changeNote.trim()) diff.note = changeNote.trim();
    return diff;
  }, [formState, changeNote, sanitizedDates, launchpadEditsLocked]);

  const validateCoreFields = useCallback(() => {
    if (!formState.mobilisation_id) {
      const msg = "Select a mobilisation to continue.";
      setFormError(msg);
      setFormErrorDetails(null);
      showToast(msg, "error");
      return false;
    }

    if (!formState.pre_assessment_start_datetime || !formState.post_assessment_datetime) {
      const msg = "Pre and post assessment schedules are required.";
      setFormError(msg);
      setFormErrorDetails(null);
      showToast(msg, "error");
      return false;
    }

    if (formState.launchpad_session_dates.filter(Boolean).length < 4) {
      const msg = "Provide at least four launchpad dates.";
      setFormError(msg);
      setFormErrorDetails(null);
      showToast(msg, "error");
      return false;
    }

    const launchpadDates = formState.launchpad_session_dates.filter(Boolean);
    const preDate = (formState.pre_assessment_start_datetime || "").slice(0, 10);
    if (preDate && launchpadDates.includes(preDate)) {
      const msg = "Pre-assessment cannot fall on the same date as any launchpad session.";
      setFormError(msg);
      setFormErrorDetails(null);
      showToast(msg, "error");
      return false;
    }

    return true;
  }, [formState, showToast]);

  const saveDraft = useCallback(async () => {
    if (!canEditProposalDraft) return;
    if (!validateCoreFields()) return;
    setFormError(null);
    setFormErrorDetails(null);
    setShowErrorDetails(false);

    const payload = {
      mobilisation_id: formState.mobilisation_id,
      center_id: formState.center_id || undefined,
      trainer_id: formState.trainer_id || undefined,
      is_center_infra_ready: formState.is_center_infra_ready,
      is_recruiter_engaged: formState.is_recruiter_engaged,
      launchpad_session_dates: sanitizedDates(formState.launchpad_session_dates),
      basecamp_dates: sanitizedDates(formState.basecamp_dates),
      domain_training_dates: sanitizedDates(formState.domain_training_dates),
      ojt_dates: sanitizedDates(formState.ojt_dates),
      pre_assessment_start_datetime: toIsoString(formState.pre_assessment_start_datetime),
      post_assessment_datetime: toIsoString(formState.post_assessment_datetime),
      batch_launch_date: formState.batch_launch_date || undefined,
      placement_drive_date: formState.placement_drive_date || undefined,
    };

    setLoading(true);
    try {
      const response = editingBatch
        ? await batchApi.updateBatch(String(editingBatch.batch_id), payload)
        : await batchApi.createBatch(payload as any);

      if (response.success) {
        showToast(response.message || "Batch saved successfully.", "success");
        await refreshBatches();
        if (editingBatch) {
          const updated = await batchApi.getBatch(String(editingBatch.batch_id));
          if (updated.success && updated.data) {
            setEditingBatch(updated.data);
            applyBatchToForm(updated.data);
          }
        } else {
          closeForm();
        }
      } else {
        const msg = response.message || "Failed to save batch.";
        setFormError(msg);
        setFormErrorDetails(msg?.includes("Details:") ? msg : null);
        showToast(msg, "error");
      }
    } catch (error: any) {
      console.error("Failed to save batch", error);
      const msg = error?.response?.data?.message || "Something went wrong while saving the batch.";
      setFormError(msg);
      setFormErrorDetails(msg?.includes("Details:") ? msg : null);
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  }, [applyBatchToForm, canEditProposalDraft, closeForm, editingBatch, formState, refreshBatches, sanitizedDates, showToast, validateCoreFields]);

  const submitForApproval = useCallback(async () => {
    if (!editingBatch) return;
    if (!canSubmitProposal) {
      showToast("Only Center Admin/Project Manager can submit.", "error");
      return;
    }
    try {
      const response = await batchApi.submitBatchProposal(editingBatch.batch_id);
      if (response.success) {
        showToast("Batch submitted for approval.", "success");
        closeForm();
        await refreshBatches();
      } else {
        showToast(response.message || "Unable to submit batch.", "error");
      }
    } catch (error) {
      console.error("Failed to submit batch", error);
      showToast("Unable to submit batch right now.", "error");
    }
  }, [canSubmitProposal, closeForm, editingBatch, refreshBatches, showToast]);

  const handleReviewProposal = useCallback(
    async (batchId: number, action: "Approved" | "Rejected", comment?: string) => {
      try {
        const response = await batchApi.reviewBatchProposal(batchId, { action, comments: comment });
        if (response.success) {
          const message = "Batch " + action.toLowerCase() + " successfully.";
          showToast(message, "success");
          await refreshBatches();
        } else {
          showToast(response.message || "Unable to process review.", "error");
        }
      } catch (error) {
        console.error("Failed to review batch", error);
        showToast("Unable to process review.", "error");
      }
    },
    [refreshBatches, showToast]
  );
  const handleStartChangeRequest = useCallback(() => {
    if (!editingBatch) return;
    changeBaselineRef.current = { ...formState };
    setChangeMode(true);
    setChangeNote("");
  }, [editingBatch, formState]);

  const handleCancelChangeRequest = useCallback(() => {
    if (changeBaselineRef.current) {
      setFormState(changeBaselineRef.current);
    }
    setChangeMode(false);
    setChangeNote("");
  }, []);

  const handleSubmitChangeRequest = useCallback(async () => {
    if (!editingBatch) return;
    const payload = buildChangeRequestPayload();
    if (!payload) {
      const msg = "No changes detected.";
      setFormError(msg);
      setFormErrorDetails(null);
      return;
    }

    setChangeSubmitting(true);
    setFormError(null);
    setFormErrorDetails(null);
    try {
      const response = await batchApi.createChangeRequest(editingBatch.batch_id, payload as Record<string, any>);
      if (response.success) {
        showToast(response.message || "Change request submitted.", "success");
        setChangeMode(false);
        setChangeNote("");
        await refreshBatches();
        const updated = await batchApi.getBatch(String(editingBatch.batch_id));
        if (updated.success && updated.data) {
          setEditingBatch(updated.data);
          applyBatchToForm(updated.data);
        }
      } else {
        const msg = response.message || "Failed to submit change request.";
        setFormError(msg);
        setFormErrorDetails(msg?.includes("Details:") ? msg : null);
      }
    } catch (error: any) {
      console.error("Failed to submit change request", error);
      const msg = error?.response?.data?.message || error?.message || "Unable to submit change request.";
      setFormError(msg);
      setFormErrorDetails(msg?.includes("Details:") ? msg : null);
    } finally {
      setChangeSubmitting(false);
    }
  }, [applyBatchToForm, buildChangeRequestPayload, editingBatch, refreshBatches, showToast]);

  const handleReviewChangeRequest = useCallback(
    async (action: "Approved" | "Rejected") => {
      if (!editingBatch || !editingBatch.change_request?.id) return;

      setChangeReviewLoading(true);
      try {
        const response = await batchApi.reviewChangeRequest(
          editingBatch.batch_id,
          editingBatch.change_request.id,
          {
            action,
            comment: changeReviewComment || undefined,
          }
        );

        if (response.success) {
          const fallbackMessage = "Change request " + action.toLowerCase() + ".";
          showToast(response.message || fallbackMessage, "success");
          setChangeReviewComment("");
          const updated = await batchApi.getBatch(String(editingBatch.batch_id));
          if (updated.success && updated.data) {
            setEditingBatch(updated.data);
            applyBatchToForm(updated.data);
          }
          await refreshBatches();
        } else {
          showToast(response.message || "Failed to review change request.", "error");
        }
      } catch (error) {
        console.error("Failed to review change request", error);
        showToast("Unable to review change request.", "error");
      } finally {
        setChangeReviewLoading(false);
      }
    },
    [applyBatchToForm, changeReviewComment, editingBatch, refreshBatches, showToast]
  );

  type ReviewDialogState = { open: boolean; batchId: number | null; action: 'Approved' | 'Rejected' | null; };
  const [reviewDialog, setReviewDialog] = useState<ReviewDialogState>({ open: false, batchId: null, action: null });
  const [reviewComment, setReviewComment] = useState('');

  const openReview = (batchId: number, action: 'Approved' | 'Rejected') => {
    setReviewComment('');
    setReviewDialog({ open: true, batchId, action });
  };
  const submitReview = async () => {
    if (!reviewDialog.batchId || !reviewDialog.action) return;
    await handleReviewProposal(reviewDialog.batchId, reviewDialog.action, reviewComment);
    setReviewDialog({ open: false, batchId: null, action: null });
  };
  const renderBatchCard = (batch: Batch) => (
    <div key={batch.batch_id} className="h-full rounded-lg border border-gray-200 bg-white p-4 shadow-sm flex flex-col">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900 break-words">{batch.batch_code || "Batch #" + batch.batch_id}</h3>
          <p className="text-sm text-gray-500 break-words">{batch.mobilisation_name || "Mobilisation not linked"}</p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-600 whitespace-nowrap">
            {batch.batch_proposal_status}
          </span>
          {batch.status && (
            <span
              className={
                "rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap " +
                (batch.status === "Cancelled"
                  ? "bg-red-50 text-red-600"
                  : batch.status === "Completed"
                  ? "bg-green-50 text-green-600"
                  : "bg-gray-100 text-gray-600")
              }
            >
              {batch.status}
            </span>
          )}
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-2 text-sm text-gray-600 md:grid-cols-2">
        <div>
          <dt className="font-medium text-gray-500">Trainer</dt>
          <dd>{batch.assignedTrainerName || "Not assigned"}</dd>
        </div>
        <div>
          <dt className="font-medium text-gray-500">Center</dt>
          <dd>{batch.assignedCenterName || "Not assigned"}</dd>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-blue-500" />
          <span>Launchpad: {batch.launchpad_session_dates && batch.launchpad_session_dates.length ? batch.launchpad_session_dates[0] : "TBD"}</span>
        </div>
        <div className="flex items-center gap-2">
          <PlayCircle className="h-4 w-4 text-blue-500" />
          <span> Send Consent: {batch.flags?.part_two_open ? "Open" : "Closed"}</span>
        </div>
      </dl>

      <div className="mt-auto flex flex-wrap gap-2 pt-4">
        <button
          type="button"
          onClick={() => openEditModal(batch)}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:text-gray-900"
        >
          <Eye className="h-4 w-4" /> View / Edit
        </button>
        {isSystemAdmin && batch.batch_proposal_status === "Submitted" && (
          <>
            <button
              type="button"
              onClick={() => openReview(batch.batch_id, "Approved")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-green-500 px-3 py-1.5 text-sm font-medium text-green-600 transition hover:bg-green-50"
            >
              <CheckCircle className="h-4 w-4" /> Approve
            </button>
            <button
              type="button"
              onClick={() => openReview(batch.batch_id, "Rejected")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-red-500 px-3 py-1.5 text-sm font-medium text-red-600 transition hover:bg-red-50"
            >
              <XCircle className="h-4 w-4" /> Reject
            </button>
          </>
        )}
      </div>

      {reviewDialog.open && (
        <ModalPortal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b p-4">
              <div className="text-lg font-semibold">
                {reviewDialog.action === 'Approved' ? 'Approve batch' : 'Reject batch'}
              </div>
              <p className="text-xs text-gray-500">Add an optional comment for the approval history.</p>
            </div>
            <div className="p-4 space-y-3">
              <textarea
                rows={4}
                value={reviewComment}
                onChange={(e) => setReviewComment(e.target.value)}
                placeholder="Type your review comment (optional)"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:ring-1 focus:ring-blue-500"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReviewDialog({ open: false, batchId: null, action: null })}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitReview}
                  className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
                >
                  Confirm
                </button>
              </div>
            </div>
            </div>
          </div>
        </ModalPortal>
      )}

    </div>
  );


  const flags: BatchFlags | null = editingBatch?.flags || null;
  const changeStats: BatchChangeRequestStats | null = editingBatch?.change_request_stats || null;
  const latestChangeRequest: BatchChangeRequestSummary | null = editingBatch?.change_request || null;

  const partTwoOpen = !!flags?.part_two_open;
  const changeWindowOpen = !!flags?.change_request_window_open;
  const pendingChange = !!flags?.pending_change_request;
  const changeWindowType = flags?.change_request_window_type ?? null;
  const changeWindowLabel = changeWindowType === 'post-launch' ? 'post-launch' : 'pre-launch';
  const postLaunchChangeMode = changeMode && changeWindowType === 'post-launch';
  const changeLimitAvailable =
    changeStats == null
      ? true
      : changeStats.limit == null
        ? true
        : (changeStats.remaining ?? 0) > 0;
  const canTriggerChangeRequest =
    editingBatch?.batch_proposal_status === "Approved" &&
    changeWindowOpen &&
    !pendingChange &&
    changeLimitAvailable &&
    !isCancelled;

  const scheduleNotices = useMemo(() => {
    const notices: Array<{ tone: 'success' | 'warning' | 'info'; message: string }> = [];
    if (proposalApproved) {
      notices.push({
        tone: 'success',
        message:
          'Proposal approved. Further updates require change requests and approval before they affect actual schedules.',
      });
    }
    if (proposalApproved && !changeWindowOpen) {
      notices.push({
        tone: 'warning',
        message: 'Change request window is closed for this batch. Dates and assignments are locked.',
      });
    }
    if (proposalApproved && pendingChange) {
      notices.push({
        tone: 'warning',
        message: 'A change request is pending review. Wait for the decision before editing again.',
      });
    }
    if (proposalApproved && changeStats) {
      const remaining =
        changeStats.limit == null || changeStats.remaining == null
          ? null
          : Math.max(0, changeStats.remaining);
      const message =
        changeStats.limit == null
          ? `Change requests used: ${changeStats.approved_count}. Post-launch adjustments are unlimited during this window.`
          : `Change requests used: ${changeStats.approved_count}. Remaining: ${remaining}. Limit across the ${changeWindowLabel} window: ${changeStats.limit}.`;
      notices.push({
        tone: 'info',
        message,
      });
    }
    if (launchpadEditsLocked) {
      notices.push({
        tone: 'info',
        message: 'Launchpad sessions have already started. These dates are now read-only for audit integrity.',
      });
    }
    if (postLaunchChangeMode) {
      notices.push({
        tone: 'info',
        message: 'Post-launch change requests cannot update center, trainer, launchpad, pre-assessment, or batch launch fields.',
      });
    }
    return notices;
  }, [proposalApproved, changeWindowOpen, pendingChange, changeStats, changeWindowLabel, launchpadEditsLocked, postLaunchChangeMode]);

  const noticeToneText: Record<'success' | 'warning' | 'info', string> = {
    success: 'text-green-700',
    warning: 'text-amber-700',
    info: 'text-blue-700',
  };
  const noticeToneDot: Record<'success' | 'warning' | 'info', string> = {
    success: 'bg-green-500',
    warning: 'bg-amber-500',
    info: 'bg-blue-500',
  };

  const canUsePartTwo = Boolean(partTwoOpen && !pendingChange && !isCancelled);

  const fetchRegisteredTrainees = useCallback(async () => {
    if (!editingBatch) {
      showToast("Open a batch first.", "error");
      return;
    }

    if (isCancelled) {
      setLaunchpadAttendance({});
      setLaunchpadSessionCount(0);
      showToast("Batch is cancelled. Trainee actions are locked.", "error");
      return;
    }

    if (!canUsePartTwo) {
      const fallbackTotal = Array.isArray(editingBatch.launchpad_session_dates)
        ? editingBatch.launchpad_session_dates.filter(Boolean).length
        : 0;
      setLaunchpadSessionCount(fallbackTotal);
      setLaunchpadAttendance({});
      showToast("Sending consent to students is not currently open for this batch.", "error");
      return;
    }

    const totalSessions = Array.isArray(editingBatch.launchpad_session_dates)
      ? editingBatch.launchpad_session_dates.filter(Boolean).length
      : 0;
    setLaunchpadAttendance({});
    setLaunchpadSessionCount(totalSessions);

    try {
      const response = await traineeRegistrationApi.getTrainees({
        mobilisation_id: editingBatch.mobilisation_id,
        limit: 2000,
      });

      if (response.success && response.data) {
        setRegisteredTrainees(response.data);
        setSelectedTraineeIds([]);
        showToast("Loaded " + response.data.length + " registered trainees.", "success");
        await loadLaunchpadAttendance(editingBatch.batch_id, totalSessions);
      } else {
        showToast(response.message || "Unable to load registered trainees.", "error");
        setLaunchpadAttendance({});
      }
    } catch (error) {
      console.error("Failed to load registered trainees", error);
      showToast("Unable to load registered trainees.", "error");
      setLaunchpadAttendance({});
    }
  }, [canUsePartTwo, editingBatch, isCancelled, loadLaunchpadAttendance, showToast]);

  const filteredRegisteredTrainees = useMemo(() => {
    if (!traineeSearch) return registeredTrainees;
    const query = traineeSearch.toLowerCase();
    return registeredTrainees.filter((entry) => {
      const name = entry.trainee.user.full_name.toLowerCase();
      const email = entry.trainee.user.email ? entry.trainee.user.email.toLowerCase() : "";
      return name.includes(query) || email.includes(query);
    });
  }, [registeredTrainees, traineeSearch]);

  const toggleTraineeSelection = useCallback((traineeId: number) => {
    if (isCancelled) {
      showToast("Batch is cancelled. Trainee list is locked.", "error");
      return;
    }
    setSelectedTraineeIds((previous) =>
      previous.includes(traineeId)
        ? previous.filter((id) => id !== traineeId)
        : [...previous, traineeId]
    );
  }, [isCancelled, showToast]);

  const formFinalList = useCallback(() => {
    if (isCancelled) {
      showToast("Batch is cancelled. Final lists cannot be prepared.", "error");
      return;
    }
    if (!selectedTraineeIds.length) {
      showToast("Select at least one trainee.", "error");
      return;
    }

    const next = registeredTrainees
      .filter((entry) => selectedTraineeIds.includes(entry.trainee.trainee_id))
      .map((entry) => ({
        trainee_id: entry.trainee.trainee_id,
        full_name: entry.trainee.user.full_name,
        email: entry.trainee.user.email,
        status: "pending" as const,
      }));

    setFinalTrainees(next);
    showToast("Prepared final list with " + next.length + " trainee(s).", "success");
  }, [isCancelled, registeredTrainees, selectedTraineeIds, showToast]);

  const sendConsent = useCallback(async () => {
    if (isCancelled) {
      showToast("Batch is cancelled. Consent emails are disabled.", "error");
      return;
    }
    if (!editingBatch || !finalTrainees.length) {
      showToast("Prepare the final list before sending consent.", "error");
      return;
    }

    const pending = finalTrainees.filter((entry) => entry.status === "pending");
    if (!pending.length) {
      showToast("Consent already sent to this list.", "error");
      return;
    }

    const centerName = centers.find((center) => center.center_id === formState.center_id)?.name;
    if (!centerName) {
      showToast("Assign a center before sending consent.", "error");
      return;
    }

    setSendingConsent(true);
    setFinalTrainees((previous) =>
      previous.map((entry) =>
        entry.status === "pending" ? { ...entry, status: "sending" as const } : entry
      )
    );

    try {
      const response = await traineeRegistrationApi.sendConsent(
        pending.map((entry) => entry.trainee_id),
        centerName,
        editingBatch.mobilisation_id
      );

      if (response.success && response.data) {
        const statusById = new Map(response.data.map((row) => [row.trainee_id, row]));
        setFinalTrainees((previous) =>
          previous.map((entry) => {
            if (statusById.has(entry.trainee_id)) {
              const info = statusById.get(entry.trainee_id)!;
              return {
                ...entry,
                status: info.status,
                error: info.error,
              };
            }
            return entry;
          })
        );
        showToast("Consent emails triggered.", "success");
      } else {
        throw new Error(response.message || "Unknown error");
      }
    } catch (error) {
      console.error("Failed to send consent", error);
      showToast("Failed to trigger consent emails.", "error");
      setFinalTrainees((previous) =>
        previous.map((entry) =>
          entry.status === "sending" ? { ...entry, status: "pending" as const } : entry
        )
      );
    } finally {
      setSendingConsent(false);
    }
  }, [centers, editingBatch, finalTrainees, formState.center_id, isCancelled, showToast]);

  const handleBatchCancellation = useCallback(async () => {
    if (!editingBatch) return;
    setCancelLoading(true);
    try {
      const response = await batchApi.cancelBatch(editingBatch.batch_id);
      if (response.success) {
        showToast(response.message || "Batch cancelled successfully.", "success");
        setCancelDialogOpen(false);
        setChangeMode(false);
        setChangeNote("");
        setChangeReviewComment("");
        setChangeReviewLoading(false);
        setChangeSubmitting(false);
        setRegisteredTrainees([]);
        setSelectedTraineeIds([]);
        setFinalTrainees([]);
        setLaunchpadAttendance({});
        setLaunchpadSessionCount(0);
        setTraineeSearch("");
        setSendingConsent(false);
        setEditingBatch((previous) =>
          previous && previous.batch_id === editingBatch.batch_id
            ? { ...previous, status: "Cancelled", batch_proposal_status: "Cancelled" }
            : previous
        );
        await refreshBatches();
      } else {
        showToast(response.message || "Unable to cancel batch.", "error");
      }
    } catch (error) {
      console.error("Failed to cancel batch", error);
      showToast("Unable to cancel batch right now.", "error");
    } finally {
      setCancelLoading(false);
    }
  }, [editingBatch, refreshBatches, showToast]);
  const renderToast = () =>
    toast ? (
      <div
        className={
          "fixed inset-x-0 top-4 z-[70] mx-auto w-full max-w-xl rounded-md border px-4 py-3 text-sm shadow-lg " +
          (toast.type === "success"
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-red-200 bg-red-50 text-red-800")
        }
      >
        {toast.message}
      </div>
    ) : null;

  const renderChangeSummary = () => {
    if (!latestChangeRequest) return null;

    const payloadEntries = Object.entries(latestChangeRequest.requested_payload || {})
      .filter(([key]) => key !== "note")
      .map(([key, value]) => ({
        key,
        label: CHANGE_FIELD_LABELS[key] || key,
        value,
      }));

    return (
      <div className="space-y-3 rounded-md border border-blue-100 bg-blue-50 p-4 text-sm">
        <div className="flex items-center justify-between">
          <div className="font-medium text-blue-800">Latest change request</div>
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-blue-600">
            {latestChangeRequest.status}
          </span>
        </div>
        <div className="space-y-2 text-blue-700">
          <div>Requested at: {new Date(latestChangeRequest.requested_at).toLocaleString()}</div>
          {latestChangeRequest.note && <div>Note: {latestChangeRequest.note}</div>}
          {payloadEntries.length > 0 && (
            <div className="space-y-1">
              <div className="font-medium">Fields requested</div>
              <ul className="list-inside list-disc space-y-1">
                {payloadEntries.map((entry) => (
                  <li key={entry.key}>
                    <span className="font-semibold">{entry.label}: </span>
                    {formatDisplayValue(entry.value)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {latestChangeRequest.review_comment && (
            <div>Review comment: {latestChangeRequest.review_comment}</div>
          )}
        </div>
      </div>
    );
  };
  const renderPartTwo = () => {
    if (isCancelled) {
      return (
        <section className="space-y-4">
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div>
              <div className="font-semibold">Batch cancelled</div>
              <p>All trainee finalisation steps are read-only for cancelled batches.</p>
            </div>
          </div>
        </section>
      );
    }

    const renderRegisteredTab = () => (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Users className="h-4 w-4" /> Step 4 &middot; Registered trainees
          </div>
          <span className="text-xs text-gray-500">
            Showing {filteredRegisteredTrainees.length} of {registeredTrainees.length}
          </span>
        </div>
        <div className="relative max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search trainees"
            value={traineeSearch}
            onChange={(event) => setTraineeSearch(event.target.value)}
            className="w-full rounded-md border border-gray-200 px-3 py-2 pl-9 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div className="max-h-60 overflow-y-auto rounded border border-gray-100">
          {filteredRegisteredTrainees.length ? (
            <table className="min-w-full divide-y divide-gray-100 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Select</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Trainee</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Email</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Launchpad</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Registered on</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredRegisteredTrainees.map((entry) => {
                  const stats = launchpadAttendance[entry.trainee.trainee_id];
                  const present = stats?.present ?? 0;
                  const recorded = stats?.recorded ?? 0;
                  const pct =
                    totalLaunchpadSessions > 0 ? Math.round((present / totalLaunchpadSessions) * 100) : 0;
                  const pending = totalLaunchpadSessions > 0 ? totalLaunchpadSessions - recorded : 0;

                  return (
                    <tr key={entry.registration_id}>
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selectedTraineeIds.includes(entry.trainee.trainee_id)}
                          onChange={() => toggleTraineeSelection(entry.trainee.trainee_id)}
                        />
                      </td>
                      <td className="px-3 py-2 text-gray-700">{entry.trainee.user.full_name}</td>
                      <td className="px-3 py-2 text-gray-500">{entry.trainee.user.email || "N/A"}</td>
                      <td className="px-3 py-2 text-gray-600">
                        {totalLaunchpadSessions > 0 ? (
                          <div className="text-xs text-gray-700">
                            <span className="font-semibold">{present}</span>
                            <span className="text-gray-400">/{totalLaunchpadSessions}</span>
                            <span className="ml-1 text-gray-500">{pct}%</span>
                            {pending > 0 && (
                              <div className="text-[10px] text-gray-400">
                                {recorded} marked / {totalLaunchpadSessions}
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">No sessions</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-500">
                        {new Date(entry.registration_date).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-sm text-gray-500">No trainees loaded yet.</div>
          )}
        </div>
      </div>
    );

    const renderFinalListTable = () => (
      <div className="max-h-52 overflow-y-auto rounded border border-gray-100">
        {finalTrainees.length ? (
          <table className="min-w-full divide-y divide-gray-100 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Trainee</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Email</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Launchpad</th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-gray-500">Consent status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {finalTrainees.map((entry) => {
                const stats = launchpadAttendance[entry.trainee_id];
                const present = stats?.present ?? 0;
                const recorded = stats?.recorded ?? 0;
                const pct =
                  totalLaunchpadSessions > 0 ? Math.round((present / totalLaunchpadSessions) * 100) : 0;
                const pending = totalLaunchpadSessions > 0 ? totalLaunchpadSessions - recorded : 0;

                return (
                  <tr key={entry.trainee_id}>
                    <td className="px-3 py-2 text-gray-700">{entry.full_name}</td>
                    <td className="px-3 py-2 text-gray-500">{entry.email || "N/A"}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {totalLaunchpadSessions > 0 ? (
                        <div className="text-xs text-gray-700">
                          <span className="font-semibold">{present}</span>
                          <span className="text-gray-400">/{totalLaunchpadSessions}</span>
                          <span className="ml-1 text-gray-500">{pct}%</span>
                          {pending > 0 && (
                            <div className="text-[10px] text-gray-400">
                              {recorded} marked / {totalLaunchpadSessions}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">No sessions</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-600">
                      <span
                        className={
                          "rounded-full px-2 py-0.5 text-xs font-semibold " +
                          (entry.status === "sent"
                            ? "bg-green-50 text-green-600"
                            : entry.status === "failed"
                            ? "bg-red-50 text-red-600"
                            : entry.status === "sending"
                            ? "bg-blue-50 text-blue-600"
                            : "bg-gray-100 text-gray-600")
                        }
                      >
                        {entry.status}
                      </span>
                      {entry.error && <div className="text-xs text-red-500">{entry.error}</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-sm text-gray-500">
            Build the final list from the registered trainees to continue.
          </div>
        )}
      </div>
    );

    const renderFinalListTab = () => (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <CheckCircle className="h-4 w-4 text-green-500" /> Step 5 &middot; Final trainee selection
          </div>
          <button
            type="button"
            onClick={formFinalList}
            disabled={!selectedTraineeIds.length}
            className={
              "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition " +
              (selectedTraineeIds.length
                ? "bg-green-500 text-white hover:bg-green-600"
                : "cursor-not-allowed bg-gray-200 text-gray-500")
            }
          >
            <CheckCircle className="h-4 w-4" /> Prepare final list
          </button>
        </div>
        {renderFinalListTable()}
      </div>
    );

    const renderConsentTab = () => (
      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Send className="h-4 w-4 text-blue-500" /> Step 6 &middot; Send consent emails
          </div>
          <button
            type="button"
            onClick={sendConsent}
            disabled={sendingConsent || !finalTrainees.length}
            className={
              "inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition " +
              (!finalTrainees.length
                ? "cursor-not-allowed bg-gray-200 text-gray-500"
                : "bg-blue-600 text-white hover:bg-blue-700") +
              (sendingConsent ? " opacity-75" : "")
            }
          >
            {sendingConsent ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sendingConsent ? "Sending" : "Send consent"}
          </button>
        </div>
        <p className="text-xs text-gray-500">
          Consent can be triggered multiple times; already contacted trainees will be skipped automatically.
        </p>
        {renderFinalListTable()}
      </div>
    );

    const activeContent = canUsePartTwo
      ? activePartTwoTab === "registered"
        ? renderRegisteredTab()
        : activePartTwoTab === "finalList"
        ? renderFinalListTab()
        : renderConsentTab()
      : (
        <div className="p-6 text-sm text-gray-500">
          Actions unlock once the consent window opens and there are no pending change requests.
        </div>
      );

    return (
      <section className="space-y-4">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <PlayCircle className="h-5 w-5 text-blue-500" /> Part 2 &middot; Trainee finalisation & consent
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Opens on Launchpad Day 4 and closes the day before the pre-assessment. Pending change requests pause all actions.
            </p>
          </div>
          <button
            type="button"
            onClick={fetchRegisteredTrainees}
            disabled={!canUsePartTwo}
            className={
              "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium shadow-sm transition " +
              (canUsePartTwo ? "border-blue-500 text-blue-600 hover:bg-blue-50" : "cursor-not-allowed border-gray-200 text-gray-400")
            }
          >
            <RefreshCw className="h-4 w-4" /> Refresh registered trainees
          </button>
        </header>

        {!partTwoOpen && (
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
            Sending consent to trainees unlocks on Launchpad Day 4 and closes the day before the pre-assessment schedule.
          </div>
        )}

        {pendingChange && (
          <div className="rounded-md border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
            A change request is pending review. Actions in Part 2 are paused until the request is closed.
          </div>
        )}

        <div className="rounded-md border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-4 py-2">
            {PART_TWO_TABS.map((tab) => {
              const isActive = activePartTwoTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => canUsePartTwo && setActivePartTwoTab(tab.id)}
                  disabled={!canUsePartTwo}
                  className={
                    "rounded-md px-3 py-1.5 text-sm font-medium transition " +
                    (isActive ? "bg-blue-600 text-white shadow-sm" : "border border-gray-200 text-gray-600 hover:bg-gray-50") +
                    (!canUsePartTwo ? " cursor-not-allowed opacity-60" : "")
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
          <div className="px-4 py-4">{activeContent}</div>
        </div>
      </section>
    );
  };
  const renderFormModal = () => {
    if (typeof document === "undefined") return null;

    return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
      <div className="w-full max-w-5xl max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-lg bg-white shadow-xl">

        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {editingBatch ? "Edit batch " + (editingBatch.batch_code || "#" + editingBatch.batch_id) : "Create batch proposal"}
            </h2>
            <p className="text-sm text-gray-500">Part 1 covers proposal creation. Change requests drive actuals once approved.</p>
          </div>
          <button
            type="button"
            onClick={closeForm}
            className="rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-6">
            <button
              onClick={() => setSelectedTab('details')}
              className={`${selectedTab === 'details'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 font-medium text-sm py-3`}
            >
              Batch Details
            </button>
            <button
              onClick={() => setSelectedTab('proposal')}
              className={`${selectedTab === 'proposal'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap border-b-2 font-medium text-sm py-3`}
            >
              Proposal Approval History
            </button>
          </nav>
        </div>


        {selectedTab === 'details' && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              saveDraft();
            }}
            className="space-y-8 px-6 py-6"
          >
            <div className="flex items-start gap-2 rounded-md border border-blue-100 bg-blue-50 p-4 text-sm text-blue-800">
              <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <div className="space-y-1">
                <div className="font-semibold">Scheduling rule</div>
                <p>
                  Dates must follow: Today &lt; max(launchpad) &lt; pre-assessment start &lt; batch launch ≤ min(basecamp) &lt;
                  max(basecamp) &lt; min(domain) &lt; max(domain) &lt; min(OJT) &lt; max(OJT) &lt; post-assessment start &lt; placement.
                  Only batch launch ≤ min(basecamp) may be equal; all others must increase.
                </p>
              </div>
            </div>
            {formError && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="font-semibold">
                    {changeMode ? "Unable to submit change request" : "Unable to save batch"}
                  </div>
                  <p className="mt-1 whitespace-pre-line">
                    {formErrorDetails && formErrorDetails.includes("Details:")
                      ? formErrorDetails.split("Details:")[0].trim()
                      : formError}
                  </p>
                  {formErrorDetails && formErrorDetails.includes("Details:") && (
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => setShowErrorDetails((prev) => !prev)}
                        className="text-xs font-medium text-blue-700 hover:underline"
                      >
                        {/* {showErrorDetails ? "Hide details" : "Show details"} */}
                      </button>
                      {/* {showErrorDetails && (
                        <pre className="whitespace-pre-wrap break-words rounded-md bg-red-100 px-3 py-2 text-xs text-red-800">
                          {formErrorDetails.substring(formErrorDetails.indexOf("Details:") + "Details:".length).trim()}
                        </pre>
                      )} */}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setFormError(null)}
                  className="text-red-500 hover:text-red-700"
                  aria-label="Dismiss error"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {editingBatch && isCancelled && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <div className="font-semibold">This batch is cancelled</div>
                  <p>Records remain read-only for audit integrity. Create a new proposal to restart.</p>
                </div>
              </div>
            )}
            {isSystemAdmin && editingBatch && !isCancelled && (
              <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-2 text-sm text-red-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                  <div>
                    <div className="font-semibold">Cancel this batch</div>
                    <p>Cancelling is immediate, stops all downstream actions, and cannot be undone.</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCancelDialogOpen(true)}
                  className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700"
                >
                  <Ban className="h-4 w-4" /> Cancel batch
                </button>
              </div>
            )}
            <section className="space-y-4">
              <header className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Plus className="h-5 w-5 text-blue-500" /> Step 1 &middot; Mobilisation
              </header>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Mobilisation</span>
                  <select
                    name="mobilisation_id"
                    value={formState.mobilisation_id || ""}
                    onChange={handleFormFieldChange}
                    disabled={!!editingBatch}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select mobilisation</option>
                    {mobilisations.map((item) => (
                      <option key={item.mobilisation_id} value={item.mobilisation_id}>
                        {item.title || "Mobilisation " + item.mobilisation_id}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </section>

            <section className="space-y-4">
              <header className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <Calendar className="h-5 w-5 text-blue-500" /> Step 2 - Proposal core details
              </header>

              {scheduleNotices.length > 0 && (
                <div className="rounded-md border border-gray-200 bg-gray-50 p-4">
                  <div className="text-sm font-semibold text-gray-700">What you need to know</div>
                  <ul className="mt-3 space-y-2">
                    {scheduleNotices.map((notice) => (
                      <li key={notice.message} className={`flex items-start gap-2 text-sm ${noticeToneText[notice.tone]}`}>
                        <span className={`mt-1 h-2 w-2 rounded-full ${noticeToneDot[notice.tone]}`} />
                        <span>{notice.message}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700 flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-500" /> Center
                  </span>
                  <select
                    name="center_id"
                    value={formState.center_id || ""}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange || postLaunchChangeMode}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select center</option>
                    {centers.map((center) => (
                      <option key={center.center_id} value={center.center_id}>
                        {center.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700 flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-500" /> Trainer
                    {selectedTrainer && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${TRAINER_TAG_BADGE_STYLES[selectedTrainer.trainer_tag]}`}
                      >
                        {selectedTrainer.trainer_tag}
                      </span>
                    )}
                  </span>
                  <select
                    name="trainer_id"
                    value={formState.trainer_id || ""}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange || postLaunchChangeMode}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">Select trainer</option>
                    {trainers.map((trainer) => (
                      <option key={trainer.trainer_id} value={trainer.trainer_id}>
                        {`${trainer.trainer_tag} - ${trainer.contact.full_name}`}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="is_center_infra_ready"
                    checked={formState.is_center_infra_ready}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                  />
                  Center infra ready
                </label>

                <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                  <input
                    type="checkbox"
                    name="is_recruiter_engaged"
                    checked={formState.is_recruiter_engaged}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                  />
                  Recruiter engaged
                </label>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                <DateInputGroup
                  label="Launchpad session dates"
                  value={formState.launchpad_session_dates}
                  onChange={(next) => handleArrayFieldChange("launchpad_session_dates", next)}
                  requiredCount={4}
                  disabled={stepTwoLocked || pendingChange || launchpadEditsLocked}
                />
                <DateInputGroup
                  label="Basecamp dates"
                  value={formState.basecamp_dates}
                  onChange={(next) => handleArrayFieldChange("basecamp_dates", next)}
                  disabled={stepTwoLocked || pendingChange}
                />
                <DateInputGroup
                  label="Domain training dates"
                  value={formState.domain_training_dates}
                  onChange={(next) => handleArrayFieldChange("domain_training_dates", next)}
                  disabled={stepTwoLocked || pendingChange}
                />
                <DateInputGroup
                  label="OJT dates"
                  value={formState.ojt_dates}
                  onChange={(next) => handleArrayFieldChange("ojt_dates", next)}
                  disabled={stepTwoLocked || pendingChange}
                />
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Pre-assessment start</span>
                  <input
                    type="datetime-local"
                    name="pre_assessment_start_datetime"
                    value={formState.pre_assessment_start_datetime}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                    required
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Post-assessment</span>
                  <input
                    type="datetime-local"
                    name="post_assessment_datetime"
                    value={formState.post_assessment_datetime}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                    required
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Batch launch date</span>
                  <input
                    type="date"
                    name="batch_launch_date"
                    value={formState.batch_launch_date}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
                <label className="space-y-1 text-sm">
                  <span className="font-medium text-gray-700">Placement drive date</span>
                  <input
                    type="date"
                    name="placement_drive_date"
                    value={formState.placement_drive_date}
                    onChange={handleFormFieldChange}
                    disabled={stepTwoLocked || pendingChange}
                    className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </label>
              </div>
            </section>


            {proposalApproved && canEditProposalDraft && (
              <section className="space-y-4">
                <header className="text-lg font-semibold text-gray-900">Change requests</header>
                {renderChangeSummary()}
                {!changeMode && canTriggerChangeRequest && (
                  <button
                    type="button"
                    onClick={handleStartChangeRequest}
                    className="inline-flex items-center gap-2 rounded-md border border-blue-500 px-3 py-1.5 text-sm font-medium text-blue-600 transition hover:bg-blue-50"
                  >
                    <Edit className="h-4 w-4" /> Request change
                  </button>
                )}

                {changeMode && (
                  <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
                    <label className="space-y-1 text-sm">
                      <span className="font-medium text-gray-700">Change request note</span>
                      <textarea
                        value={changeNote}
                        onChange={(event) => setChangeNote(event.target.value)}
                        rows={3}
                        className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Explain why the change is required"
                      />
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={handleSubmitChangeRequest}
                        disabled={changeSubmitting}
                        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-75"
                      >
                        {changeSubmitting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        Submit for approval
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelChangeRequest}
                        className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                      >
                        <XCircle className="h-4 w-4" /> Cancel
                      </button>
                    </div>
                  </div>
                )}

                {isSystemAdmin && !isCancelled && latestChangeRequest && latestChangeRequest.status === "Pending" && (
                  <div className="space-y-3 rounded-md border border-purple-200 bg-purple-50 p-4">
                    <div className="text-sm font-medium text-purple-800">System admin review</div>
                    <textarea
                      value={changeReviewComment}
                      onChange={(event) => setChangeReviewComment(event.target.value)}
                      rows={3}
                      className="w-full rounded-md border border-purple-200 px-3 py-2 text-sm shadow-sm focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500"
                      placeholder="Optional review comment"
                    />
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleReviewChangeRequest("Approved")}
                        disabled={changeReviewLoading}
                        className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-75"
                      >
                        {changeReviewLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                        Approve
                      </button>
                      <button
                        type="button"
                        onClick={() => handleReviewChangeRequest("Rejected")}
                        disabled={changeReviewLoading}
                        className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-75"
                      >
                        {changeReviewLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <XCircle className="h-4 w-4" />}
                        Reject
                      </button>
                    </div>
                  </div>
                )}
              </section>
            )}

            {proposalApproved && renderPartTwo()}

            <footer className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
              <div className="text-xs text-gray-500">
                Planned dates apply before approval. Actuals are captured through approved change requests for audit.
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={closeForm}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
                >
                  <X className="h-4 w-4" /> Close
                </button>
                {canEditProposalDraft && !isCancelled && (
                  <>
                    {editingBatch?.batch_proposal_status !== "Approved" && (<button
                      type="submit"
                      className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
                    >
                      <SaveIcon /> Save draft
                    </button>)}
                    {canSubmitProposal && editingBatch && (editingBatch.batch_proposal_status === "Draft" || editingBatch.batch_proposal_status === "Rejected") && (
                      <button
                        type="button"
                        onClick={submitForApproval}
                        className="inline-flex items-center gap-2 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-green-700"
                      >
                        <Send className="h-4 w-4" /> Submit for approval
                      </button>
                    )}
                  </>
                )}
              </div>
            </footer>
          </form>
        )}
        {selectedTab === 'proposal' && (
          <div className="p-6 space-y-4">
            {editingBatch?.batch_proposal_status === 'Rejected' && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                Proposal was rejected
                {editingBatch?.batch_proposal_rejection_reason && (
                  <> &mdash; {editingBatch.batch_proposal_rejection_reason}</>
                )}
                . You can edit and re-submit.
              </div>
            )}

            <h4 className="text-sm font-medium text-gray-500">Approval History</h4>

            {editingBatch?.approval_history && editingBatch.approval_history.length > 0 ? (
              <div className="divide-y divide-gray-200 rounded-md border border-gray-200 bg-white">
                {editingBatch.approval_history
                  .slice(0, showAllApprovals ? editingBatch.approval_history.length : 3)
                  .map((entry, i) => (
                    <div key={entry.reviewed_at ?? i} className="p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{entry.action}</span>
                        <span className="text-gray-500">
                          {new Date(entry.reviewed_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mt-1">
                        {entry.comments || 'No comments provided.'}
                      </p>
                    </div>
                  ))}
                {editingBatch.approval_history.length > 3 && (
                  <div className="text-center p-3">
                    <button
                      onClick={() => setShowAllApprovals(!showAllApprovals)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      {showAllApprovals ? 'Show Less' : 'Show All'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-sm">No approval history yet.</div>
            )}
          </div>
        )}



      </div>
      {cancelDialogOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b p-4">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                <AlertTriangle className="h-5 w-5 text-red-600" /> Cancel batch
              </div>
              <p className="mt-1 text-sm text-gray-600">
                This marks the batch as cancelled immediately. The action cannot be undone and future scheduling is locked.
              </p>
            </div>
            <div className="space-y-4 p-4">
              <p className="text-sm text-gray-700">
                Proceed only if the batch will not run. Trainee communications, assessments, and placement tracking will halt for this batch.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => !cancelLoading && setCancelDialogOpen(false)}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-75"
                  disabled={cancelLoading}
                >
                  Keep batch
                </button>
                <button
                  type="button"
                  onClick={handleBatchCancellation}
                  disabled={cancelLoading}
                  className="inline-flex items-center gap-2 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-75"
                >
                  {cancelLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Confirm cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
    );
  };

  return (
    <div className="space-y-6">
      {renderToast()}
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Batches Management</h1>
          <p className="text-sm text-gray-500">
            Two-part flow. Create and approve proposals, then execute trainee finalisation within the allowed window.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={refreshBatches}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
          {canEditProposalDraft && (
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white transition hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" /> New proposal
            </button>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3 rounded-md border border-gray-200 bg-white p-4 shadow-sm">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by code, mobilisation, center, trainer"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="w-64 rounded-md border border-gray-200 px-3 py-2 pl-9 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <select
          value={proposalFilter}
          onChange={(event) => setProposalFilter(event.target.value)}
          className="rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All proposal statuses</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          className="rounded-md border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">All batch statuses</option>
          <option value="Draft">Draft</option>
          <option value="InProgress">In progress</option>
          <option value="Completed">Completed</option>
          <option value="Cancelled">Cancelled</option>
        </select>
      </div>

      <section className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center rounded-md border border-gray-200 bg-white p-8 text-gray-500 shadow-sm">
            <RefreshCw className="mr-2 h-5 w-5 animate-spin" /> Loading batches...
          </div>
        ) : filteredBatches.length ? (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {filteredBatches.map((batch) => renderBatchCard(batch))}
          </div>
        ) : (
          <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500 shadow-sm">
            No batches found for the selected filters.
          </div>
        )}
      </section>

      {isFormOpen && renderFormModal()}
    </div>
  );
};

const SaveIcon: React.FC = () => (
  <svg
    className="h-4 w-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h5a2 2 0 012 2v1" />
  </svg>
);

export default BatchManagement;
